/**
 * Message Input Component
 * 
 * User input component for the Brand Consultant chat interface.
 * Handles message sending, loading states, and credit validation.
 */

import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, Send, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MessageInputProps {
  onSendMessage: (message: string) => Promise<boolean>;
  isLoading: boolean;
  hasAvailableCredits: boolean;
  messageCost: number;
  creditBalance: number;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function MessageInput({
  onSendMessage,
  isLoading,
  hasAvailableCredits,
  messageCost,
  creditBalance,
  disabled = false,
  placeholder = "Ask me anything about your brand strategy, content performance, or marketing insights...",
  className
}: MessageInputProps) {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim() || isLoading || disabled) return;
    
    const message = input.trim();
    setInput('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    const success = await onSendMessage(message);
    
    // If sending failed, restore the message
    if (!success) {
      setInput(message);
    }
  }, [input, isLoading, disabled, onSendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isLoading && !disabled && hasAvailableCredits) {
        handleSubmit(e as unknown as React.FormEvent);
      }
    }
  }, [input, isLoading, disabled, hasAvailableCredits, handleSubmit]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  }, []);

  const isSubmitDisabled = !input.trim() || isLoading || disabled || !hasAvailableCredits;

  return (
    <form onSubmit={handleSubmit} className={cn("space-y-3", className)}>
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isLoading || disabled}
          className={cn(
            "min-h-[60px] max-h-[120px] resize-none pr-12",
            "border-slate-200 dark:border-slate-700",
            "focus:border-primary focus:ring-1 focus:ring-primary",
            !hasAvailableCredits && "border-destructive/50 bg-destructive/5"
          )}
          rows={2}
        />
        
        <Button
          type="submit"
          size="sm"
          disabled={isSubmitDisabled}
          className={cn(
            "absolute bottom-2 right-2 h-8 w-8 p-0",
            "shadow-sm hover:shadow-md transition-shadow"
          )}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Credit and hint information */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          <span className="hidden sm:inline">Press Enter to send, Shift+Enter for new line</span>
          <span className="sm:hidden">Enter to send</span>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn(
            "px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap",
            hasAvailableCredits 
              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
          )}>
            {messageCost} credits per message
          </span>
          
          <span className="text-muted-foreground whitespace-nowrap">
            Balance: {creditBalance}
          </span>
        </div>
      </div>

      {!hasAvailableCredits && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
          <div className="text-sm text-destructive">
            <span className="font-medium">Insufficient credits.</span>
            {' '}You need {messageCost} credits to send a message. Please purchase more credits to continue.
          </div>
        </div>
      )}
    </form>
  );
}